
import { useState, useEffect, useMemo } from 'react';
import { Constituency } from '@/types/geography';
import { Country } from '@/contexts/country/types';

interface UseConstituenciesPaginationProps {
  constituencies: Constituency[];
  availableCountries: Country[];
  initialItemsPerPage?: number;
  initialPage?: number;
}

type SortDirection = 'asc' | 'desc';

export const useConstituenciesPagination = ({
  constituencies,
  availableCountries,
  initialItemsPerPage = 10,
  initialPage = 1
}: UseConstituenciesPaginationProps) => {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);
  
  // Filtering state
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Sorting state
  const [sortField, setSortField] = useState<keyof Constituency>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, countryFilter, statusFilter]);

  // Apply filters and sorting
  const filteredConstituencies = useMemo(() => {
    let result = [...constituencies];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(constituency => 
        constituency.name.toLowerCase().includes(searchLower) || 
        constituency.code.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply country filter
    if (countryFilter !== 'all') {
      result = result.filter(constituency => constituency.country_code === countryFilter);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(constituency => 
        statusFilter === 'active' ? constituency.active : !constituency.active
      );
    }
    
    // Sort the filtered results
    result.sort((a, b) => {
      const fieldA = a[sortField];
      const fieldB = b[sortField];
      
      // Handle different field types
      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc' 
          ? fieldA.localeCompare(fieldB) 
          : fieldB.localeCompare(fieldA);
      }
      
      if (fieldA === fieldB) return 0;
      
      // Handle null/undefined
      if (fieldA == null) return sortDirection === 'asc' ? -1 : 1;
      if (fieldB == null) return sortDirection === 'asc' ? 1 : -1;
      
      // Default comparison
      return sortDirection === 'asc'
        ? fieldA < fieldB ? -1 : 1
        : fieldA > fieldB ? -1 : 1;
    });
    
    return result;
  }, [constituencies, searchTerm, countryFilter, statusFilter, sortField, sortDirection]);
  
  // Calculate pagination values
  const totalItems = filteredConstituencies.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  
  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedConstituencies = filteredConstituencies.slice(startIndex, endIndex);
  
  // Handle sort toggling
  const handleSort = (field: keyof Constituency) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to ascending for new sort field
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get country name from code
  const getCountryName = (countryCode: string): string => {
    const country = availableCountries.find(c => c.code === countryCode);
    return country ? country.name : countryCode;
  };
  
  return {
    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    paginatedConstituencies,
    
    // Filtering
    searchTerm,
    setSearchTerm,
    countryFilter,
    setCountryFilter,
    statusFilter,
    setStatusFilter,
    
    // Sorting
    sortField,
    sortDirection,
    handleSort,
    
    // Helpers
    resetFilters: () => {
      setSearchTerm('');
      setCountryFilter('all');
      setStatusFilter('all');
    },
    getCountryName
  };
};
